import { queryOptions, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  getPeopleManager,
  getPeopleManagerDirectReports,
  getPeopleManagersCount,
  PeopleManagerDirectReportsResponse,
  PeopleManagerResponse,
  PeopleManagersCountResponse,
} from 'src/queries/organizations/peopleManagers.network'
import { orgQueryKeys } from 'src/queries/queries.constants'
import {
  ExtendBaseQueryOptions,
  MutationMethod,
  useExtendMutation,
} from 'src/queries/queries.utils'
import { BackendErrorPayload } from 'src/utils/error.utils'

// Re-exported for backwards compatibility: these response shapes now live in
// the pure network layer, but external importers still reference them here.
export type { PeopleManagerDirectReportsResponse, PeopleManagerResponse }

// Error-contract parity with the legacy useExtendQuery wrapper: consumers of
// these hooks read `.error` as the raw backend payload (never
// `.error.response.data` themselves) -- useExtendQuery achieved that by
// internally reading `query.error?.response?.data` off the raw axios error.
// The migrated network layer runs through getResponseData, which normalizes
// failures into a QueryError that has NO `.response` -- only a
// non-enumerable `.originalError` holding the raw axios error. Unwrap that
// original error before reading `.response?.data` so the exposed `.error`
// value stays identical to the legacy contract. A naive `error.response?.data`
// would return undefined on every failure (a silent regression).
const toBackendErrorPayload = (
  error: unknown,
): BackendErrorPayload<string> | undefined => {
  if (!error || typeof error !== 'object') {
    return undefined
  }
  const source = ((error as { originalError?: unknown }).originalError ??
    error) as { response?: { data?: BackendErrorPayload<string> } }
  return source.response?.data
}

export interface GetPeopleManagerRequest extends ExtendBaseQueryOptions {
  organizationId: string | undefined
  userId: string | undefined
}

interface PutPeopleManagerBody {
  managerId?: string
}

export interface PutPeopleManagerRequest {
  organizationId: string
  userId: string
  body: PutPeopleManagerBody
}

export const peopleManagerQueries = {
  detail: (organizationId: string | undefined, userId: string | undefined) =>
    queryOptions({
      queryKey: orgQueryKeys.peopleManagers(organizationId, userId),
      queryFn: () => {
        if (!organizationId || !userId) {
          throw new Error(
            'peopleManagerQueries.detail: organizationId and userId are required',
          )
        }
        return getPeopleManager(organizationId, userId)
      },
      enabled: organizationId !== undefined && userId !== undefined,
    }),
  directReports: (
    organizationId: string | undefined,
    userId: string | undefined,
  ) =>
    queryOptions({
      queryKey: orgQueryKeys.peopleManagerDirectReports(organizationId, userId),
      queryFn: () => {
        if (!organizationId || !userId) {
          throw new Error(
            'peopleManagerQueries.directReports: organizationId and userId are required',
          )
        }
        return getPeopleManagerDirectReports(organizationId, userId)
      },
      enabled: organizationId !== undefined && userId !== undefined,
      select: (data: PeopleManagerDirectReportsResponse): string[] =>
        data.directReports || [],
    }),
  count: (organizationId: string | undefined) =>
    queryOptions({
      queryKey: orgQueryKeys.peopleManagerCount(organizationId),
      queryFn: () => {
        if (!organizationId) {
          throw new Error(
            'peopleManagerQueries.count: organizationId is required',
          )
        }
        return getPeopleManagersCount(organizationId)
      },
      enabled: organizationId !== undefined,
      select: (data: PeopleManagersCountResponse): number => data.count || 0,
    }),
}

export const useQueryPeopleManager = ({
  organizationId,
  userId,
  enabled = true,
  queryCacheOptions,
}: GetPeopleManagerRequest) => {
  const query = useQuery({
    ...peopleManagerQueries.detail(organizationId, userId),
    ...queryCacheOptions,
    // Preserve the factory's arg-presence guard when applying the caller's
    // `enabled` (spread overrides replace `enabled`, they don't merge it).
    enabled: enabled && organizationId !== undefined && userId !== undefined,
  })
  return { ...query, error: toBackendErrorPayload(query.error) }
}

export const usePutPeopleManager = () => {
  const queryClient = useQueryClient()
  return useExtendMutation<PeopleManagerResponse, PutPeopleManagerRequest>(
    MutationMethod.PUT,
    {
      mutationUrl: ({ organizationId, userId }) =>
        `/organizations/${organizationId}/members/${userId}/managerassignment`,
      onSuccess: ({ managerAssignment }) => {
        queryClient.invalidateQueries({
          queryKey: orgQueryKeys.peopleManagers(
            managerAssignment.organizationId,
            managerAssignment.userId,
          ),
        })
      },
    },
  )
}

export interface GetPeopleManagerCountRequest extends ExtendBaseQueryOptions {
  organizationId: string | undefined
}

export const useQueryPeopleManagerDirectReports = ({
  organizationId,
  userId,
  enabled = true,
  queryCacheOptions,
}: GetPeopleManagerRequest) => {
  const query = useQuery({
    ...peopleManagerQueries.directReports(organizationId, userId),
    ...queryCacheOptions,
    enabled: enabled && organizationId !== undefined && userId !== undefined,
  })
  return { ...query, error: toBackendErrorPayload(query.error) }
}

export const useQueryPeopleManagersCount = ({
  organizationId,
  enabled = true,
  queryCacheOptions,
}: GetPeopleManagerCountRequest) => {
  const query = useQuery({
    ...peopleManagerQueries.count(organizationId),
    ...queryCacheOptions,
    enabled: enabled && organizationId !== undefined,
  })
  return { ...query, error: toBackendErrorPayload(query.error) }
}
